import { formatDateTime } from '@if/core/utils'
import { Chip } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { DevToolsCommandExecution } from '@src/types/dev-tools'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface UseCommandHistoryColumnsOptions {
  includeErrorMessage: boolean
}

export const useCommandHistoryColumns = ({
  includeErrorMessage,
}: UseCommandHistoryColumnsOptions): GridColDef<DevToolsCommandExecution>[] => {
  const { t } = useTranslation()

  return useMemo<GridColDef<DevToolsCommandExecution>[]>(() => {
    const columns: GridColDef<DevToolsCommandExecution>[] = [
      {
        field: 'executedOn',
        headerName: t('admin.devTools.history.columns.executedOn'),
        sortable: false,
        disableColumnMenu: true,
        minWidth: 170,
        valueFormatter: (value: string) => (value ? formatDateTime(new Date(value)) : ''),
      },
      {
        field: 'executedByUserFullname',
        headerName: t('admin.devTools.history.columns.user'),
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'status',
        headerName: t('admin.devTools.history.columns.status'),
        sortable: false,
        disableColumnMenu: true,
        minWidth: 110,
        renderCell: ({ row }) => {
          const isSuccess = !row.errorMessage
          return (
            <Chip
              label={t(isSuccess ? 'admin.devTools.history.status.success' : 'admin.devTools.history.status.failed')}
              color={isSuccess ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
          )
        },
      },
    ]

    if (includeErrorMessage) {
      columns.push({
        field: 'errorMessage',
        headerName: t('admin.devTools.history.columns.errorMessage'),
        sortable: false,
        disableColumnMenu: true,
        flex: 2,
        minWidth: 200,
        valueGetter: (_value, row) => row.errorMessage ?? '',
      })
    }

    return columns
  }, [includeErrorMessage, t])
}
